import React, { useState, useEffect, useMemo, SVGProps } from 'react';
import CalendarHeatmap, { ReactCalendarHeatmapValue, ReactCalendarHeatmapDate } from 'react-calendar-heatmap';
import 'react-calendar-heatmap/dist/styles.css';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { ActivityHeatmapData } from '../../types';
import { format, parseISO } from 'date-fns';

interface ActivityHeatmapProps {
  data: ActivityHeatmapData[];
  isLoading?: boolean;
  error?: string | null;
}

// Simplified value type expected by the library's props 
interface LibraryHeatmapValue {
    date: string; 
    count: number; 
}

const CURRENT_YEAR = new Date().getFullYear();
const MIN_YEAR = 2025;
const MAX_YEAR = 2027;

const ActivityHeatmap: React.FC<ActivityHeatmapProps> = ({ data, isLoading = false, error = null }) => {
  const [selectedYear, setSelectedYear] = useState<number>(CURRENT_YEAR);

  // Filter data based on selected year
  const yearlyData = useMemo(() => {
    return data.filter(item => item.date.startsWith(selectedYear.toString()));
  }, [data, selectedYear]);

  // Find max completed count for relative scaling
  const maxCompletedCount = useMemo(() => {
      return Math.max(1, ...yearlyData.map(item => item.completed_count)); 
  }, [yearlyData]);

  const startDate = new Date(selectedYear, 0, 1); 
  const endDate = new Date(selectedYear, 11, 31);

  const handlePreviousYear = () => {
    setSelectedYear(prevYear => Math.max(MIN_YEAR, prevYear - 1));
  };

  const handleNextYear = () => {
    setSelectedYear(prevYear => Math.min(MAX_YEAR, prevYear + 1));
  };

  // Revert to simple {date, count} structure for values prop
  const heatmapValues = useMemo(() => {
      return yearlyData.map(item => ({ 
          date: item.date, 
          count: item.completed_count,
      }));
  }, [yearlyData]);

  return (
    <Card className="mt-4">
      <CardHeader>
        <div className="flex justify-between items-center mb-2"> {/* Year Navigation */} 
          <Button 
            variant="outline" 
            size="icon" 
            onClick={handlePreviousYear} 
            disabled={selectedYear <= MIN_YEAR}
            aria-label="Previous year"
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <CardTitle className="text-center">Activity Heatmap - {selectedYear}</CardTitle>
          <Button 
            variant="outline" 
            size="icon" 
            onClick={handleNextYear} 
            disabled={selectedYear >= MAX_YEAR}
            aria-label="Next year"
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
         <CardDescription>Task completion for {selectedYear}. Intensity based on completed tasks.</CardDescription>
      </CardHeader>
      <CardContent>
         {isLoading && <p>Loading heatmap...</p>} 
         {error && <p className="text-red-500">{error}</p>}
         {!isLoading && !error && (
            <div className="overflow-x-auto p-1 min-h-[150px]">
              <CalendarHeatmap
                 startDate={startDate}
                 endDate={endDate}
                 values={heatmapValues} // Use the reverted simple values
                 classForValue={(value: ReactCalendarHeatmapValue<ReactCalendarHeatmapDate> | undefined) => {
                    const count = (value as any)?.count ?? 0;
                    if (!value || count <= 0) { 
                       return 'color-empty';
                    }
                    const intensityLevel = Math.ceil((count / maxCompletedCount) * 4);
                    return `color-scale-${Math.max(1, Math.min(intensityLevel, 4))}`; 
                 }}
                 titleForValue={(value: ReactCalendarHeatmapValue<ReactCalendarHeatmapDate> | undefined) => {
                    const count = (value as any)?.count ?? 0;
                    const dateStr = (value as any)?.date ?? 'N/A';
                    return value ? `${dateStr}: ${count} completed` : 'No data';
                 }}
                 showWeekdayLabels
              />
            </div>
         )}
      </CardContent>
    </Card>
  );
};

export default ActivityHeatmap;